/**
 * Warehouse Task React Query Hooks
 *
 * React Query hooks for warehouse task management operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type {
  WarehouseTask,
  CreateWarehouseTaskDto,
  UpdateWarehouseTaskDto,
  TaskKpis,
  WarehouseTaskType,
} from '@lognai/plugin-warehouse';
import * as taskApi from '../api/tasks';
import type { PaginatedTaskList, TaskFilterParams } from '../api/tasks';
import { taskKeys } from './query-keys';

/**
 * Get tasks for a warehouse
 *
 * Supports structured filtering including ref filters for user-friendly search
 */
export const useTasksByWarehouse = (
  warehouseId: string,
  params?: TaskFilterParams,
  options?: Omit<UseQueryOptions<PaginatedTaskList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: taskKeys.byWarehouse(warehouseId, params),
    queryFn: () => taskApi.getTasksByWarehouse(warehouseId, params),
    enabled: !!warehouseId,
    ...options,
  });
};

/**
 * Get tasks assigned to a user
 *
 * Supports structured filtering including ref filters for user-friendly search
 */
export const useTasksByAssignee = (
  userId: string,
  params?: TaskFilterParams,
  options?: Omit<UseQueryOptions<PaginatedTaskList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: taskKeys.byAssignee(userId, params),
    queryFn: () => taskApi.getTasksByAssignee(userId, params),
    enabled: !!userId,
    ...options,
  });
};

/**
 * Get all tasks across all warehouses
 *
 * Supports structured filtering including ref filters for user-friendly search
 */
export const useAllTasks = (
  params?: TaskFilterParams,
  options?: Omit<UseQueryOptions<PaginatedTaskList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: taskKeys.allWarehouses(params),
    queryFn: () => taskApi.getAllTasks(params),
    ...options,
  });
};

/**
 * Get aggregated task KPIs across all warehouses
 */
export const useAllTaskKpis = (
  options?: Omit<UseQueryOptions<TaskKpis>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: taskKeys.allWarehousesKpis(),
    queryFn: () => taskApi.getAllTaskKpis(),
    ...options,
  });
};

/**
 * Get task KPIs for tasks assigned to user
 */
export const useTasksByAssigneeKpis = (
  userId: string,
  options?: Omit<UseQueryOptions<TaskKpis>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: taskKeys.byAssigneeKpis(userId),
    queryFn: () => taskApi.getTasksByAssigneeKpis(userId),
    enabled: !!userId,
    ...options,
  });
};

/**
 * Get a single task by ID
 */
export const useTask = (
  id: string,
  options?: Omit<UseQueryOptions<WarehouseTask>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => taskApi.getTask(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Create a new warehouse task
 */
export const useCreateTask = (
  options?: Omit<UseMutationOptions<WarehouseTask, Error, CreateWarehouseTaskDto>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: taskApi.createTask,
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate tasks by warehouse
      queryClient.invalidateQueries({
        queryKey: taskKeys.byWarehouse(data.warehouseId),
      });
      // Invalidate all tasks (for aggregated view)
      queryClient.invalidateQueries({
        queryKey: taskKeys.allWarehouses(),
      });
      // Invalidate tasks by assignee if assigned
      if (data.assignedTo) {
        queryClient.invalidateQueries({
          queryKey: taskKeys.byAssignee(data.assignedTo.id),
        });
      }
      // Invalidate task KPIs
      queryClient.invalidateQueries({
        queryKey: taskKeys.kpis(data.warehouseId),
      });
      // Invalidate aggregated KPIs
      queryClient.invalidateQueries({
        queryKey: taskKeys.allWarehousesKpis(),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
};

/**
 * Update an existing warehouse task
 */
export const useUpdateTask = (
  options?: Omit<
    UseMutationOptions<WarehouseTask, Error, { id: string; dto: UpdateWarehouseTaskDto }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }) => taskApi.updateTask(id, dto),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate specific task
      queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.id) });
      // Invalidate tasks by warehouse
      queryClient.invalidateQueries({
        queryKey: taskKeys.byWarehouse(data.warehouseId),
      });
      // Invalidate all tasks (for aggregated view)
      queryClient.invalidateQueries({
        queryKey: taskKeys.allWarehouses(),
      });
      // Invalidate tasks by assignee if assigned
      if (data.assignedToId) {
        queryClient.invalidateQueries({
          queryKey: taskKeys.byAssignee(data.assignedToId),
        });
        // Invalidate assignee KPIs
        queryClient.invalidateQueries({
          queryKey: taskKeys.byAssigneeKpis(data.assignedToId),
        });
      }
      // Invalidate task KPIs
      queryClient.invalidateQueries({
        queryKey: taskKeys.kpis(data.warehouseId),
      });
      // Invalidate aggregated KPIs
      queryClient.invalidateQueries({
        queryKey: taskKeys.allWarehousesKpis(),
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
};

/**
 * Delete a warehouse task
 */
export const useDeleteTask = (
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: taskApi.deleteTask,
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate all task queries
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      // Remove specific task from cache
      queryClient.removeQueries({ queryKey: taskKeys.detail(variables) });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
};

/**
 * Get task KPIs for a warehouse
 */
export const useTaskKpis = (
  warehouseId: string,
  options?: Omit<UseQueryOptions<TaskKpis>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: taskKeys.kpis(warehouseId),
    queryFn: () => taskApi.getTaskKpis(warehouseId),
    enabled: !!warehouseId,
    ...options,
  });
};

/**
 * Get tasks filtered by workflow type (convenience hook)
 *
 * Supports structured filtering including ref filters for user-friendly search:
 * - filter.warehouse: { name?: string } - Search by warehouse name
 * - filter.assignee: { name?: string } - Search by assignee name
 *
 * @param warehouseId - Warehouse to fetch tasks from
 * @param workflowType - Task type filter (passed for query key, but should be in params.filter)
 * @param params - Pagination + filter params (type should be in filter)
 */
export const useWorkflowTasks = (
  warehouseId: string,
  _workflowType: WarehouseTaskType,
  params?: TaskFilterParams,
  options?: Omit<UseQueryOptions<PaginatedTaskList>, 'queryKey' | 'queryFn'>
) => {
  return useTasksByWarehouse(
    warehouseId,
    params,
    options
  );
};
